import { useEffect, useMemo, type ReactNode } from "react";
import { enrichCanonicalSearchResponse } from "@/lib/canonical-transport";
import { InstantSearch, useInstantSearchContext, useRefinementList } from "react-instantsearch";
import { guardSearchDisposal } from "@/lib/search-disposal";
import { liteClient as algoliasearch } from "algoliasearch/lite";
import { ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY, ALGOLIA_INDEX_NAME } from "@/lib/algolia";
import { sortIndexName, ALGOLIA_ATTRS } from "@workspace/algolia-contract";
import { history } from "instantsearch.js/es/lib/routers";
import type { UiState, IndexUiState } from "instantsearch.js";
import { runSupabaseFallback, withTimeout, shouldFallback, reportFallbackTrigger, clearFallback } from "@/lib/searchFallback";
import { replaceStateIfChanged } from "@/lib/historyWrite";
import {
  captureAlgoliaRequestStamps,
  extractAlgoliaRequests,
  stampAlgoliaSearchResponse,
} from "@/lib/algolia-search-request";
import { bacActivityIndexFor, bacLocalityParams } from "@workspace/algolia-contract";
import { expandAlgoliaMakeSearchArgs, requiresCanonicalMakeSearch } from "@workspace/algolia-contract/make-search-aliases";

type RouteState = Record<string, string | string[] | undefined>;

const _customRouteStore: Record<string, string | undefined> = {};

function _initStoreFromUrl() {
  if (typeof window === 'undefined') return;
  const sp = new URLSearchParams(window.location.search);
  for (const k of ['location', 'date_from', 'date_to', 'passengers', 'radius']) {
    const v = sp.get(k);
    if (v) _customRouteStore[k] = v;
  }
}
_initStoreFromUrl();

export function setCustomRouteParam(key: string, value: string | undefined) {
  if (value === undefined || value === '') {
    delete _customRouteStore[key];
  } else {
    _customRouteStore[key] = value;
  }
}

export function clearCustomRouteParams() {
  for (const key of Object.keys(_customRouteStore)) {
    delete _customRouteStore[key];
  }
}

export function stateToRoute(uiState: UiState): RouteState {
  const indexState: IndexUiState = uiState[ALGOLIA_INDEX_NAME] || {};
  const route: Record<string, string | string[] | undefined> = {};

  if (indexState.query) route.q = indexState.query;

  const refinements = indexState.refinementList || {};
  if (refinements.rental_driving_active?.includes("true")) route.rental_type = "driving";
  else if (refinements.rental_chauffeured_active?.includes("true")) route.rental_type = "chauffeured";
  else if (refinements.rental_event_active?.includes("true")) route.rental_type = "event";

  if (refinements.make?.length) route.make = refinements.make[0];
  if (refinements[ALGOLIA_ATTRS.countryCode]?.length) {
    route.country_code = refinements[ALGOLIA_ATTRS.countryCode];
  }

  if (refinements.car_types?.length) route.car_type = refinements.car_types;

  const features: string[] = [];
  if (refinements.instant_book?.includes("true")) features.push("instant");
  if (refinements.convertible?.includes("true")) features.push("convertible");
  if (refinements.automatic?.includes("true")) features.push("automatic");
  // Task #1867 (S2): the InstantSearch refinement attribute is now
  // `has_aircon` (matches the Algolia document field + DB column). The
  // routing key in shareable URLs stays `air_con` for backwards-compat
  // so existing share-links keep working through the rename.
  if (refinements.has_aircon?.includes("true")) features.push("air_con");
  if (features.length) route.features = features;

  // The price slider may be bound to any of four attributes depending on
  // rental type (waterfall when none picked) — see useAlgoliaPriceRange and
  // task #1537. Read whichever is set; rental_type already encodes which
  // attribute will be active on the round trip.
  const range = indexState.range || {};
  const PRICE_RANGE_KEYS = [
    "price_driving",
    "price_event",
    "chauffeured_min_price_hourly_rate",
    "filter_price_per_day",
  ] as const;
  for (const key of PRICE_RANGE_KEYS) {
    const v = range[key];
    if (v) {
      const parts = String(v).split(":");
      if (parts[0] && parts[0] !== "") route.price_min = parts[0];
      if (parts[1] && parts[1] !== "") route.price_max = parts[1];
      break;
    }
  }

  if (indexState.page && indexState.page > 1) route.page = String(indexState.page);
  if (indexState.sortBy && indexState.sortBy !== ALGOLIA_INDEX_NAME) {
    const sortMap: Record<string, string> = {
      [sortIndexName(ALGOLIA_INDEX_NAME, "price_asc")]: "price_asc",
      [sortIndexName(ALGOLIA_INDEX_NAME, "price_desc")]: "price_desc",
      [sortIndexName(ALGOLIA_INDEX_NAME, "newest")]: "newest",
      [sortIndexName(ALGOLIA_INDEX_NAME, "top_rated")]: "top_rated",
    };
    const sortVal = sortMap[indexState.sortBy];
    if (sortVal) route.sort = sortVal;
  }

  if (_customRouteStore.location) route.location = _customRouteStore.location;
  if (_customRouteStore.date_from) route.date_from = _customRouteStore.date_from;
  if (_customRouteStore.date_to) route.date_to = _customRouteStore.date_to;
  if (refinements.eras?.length) route.era = refinements.eras;
  // Curated colour multi-select (task #1835). Serialise selected colour
  // ids as a single comma-joined `colours` param so shareable URLs stay
  // compact even when several colours are picked.
  if (refinements.colour_id?.length) route.colours = refinements.colour_id.join(",");
  if (_customRouteStore.passengers) route.passengers = _customRouteStore.passengers;
  if (_customRouteStore.radius) route.radius = _customRouteStore.radius;

  return route;
}

export function routeToState(route: RouteState): UiState {
  const refinementList: Record<string, string[]> = {};

  if (route.rental_type === "driving") refinementList.rental_driving_active = ["true"];
  else if (route.rental_type === "chauffeured") refinementList.rental_chauffeured_active = ["true"];
  else if (route.rental_type === "event") refinementList.rental_event_active = ["true"];

  if (route.make) refinementList.make = [route.make as string];

  const carTypes = Array.isArray(route.car_type) ? route.car_type : route.car_type ? [route.car_type] : [];
  if (carTypes.length) refinementList.car_types = carTypes;

  const countryCodes = Array.isArray(route.country_code)
    ? route.country_code
    : route.country_code
      ? [route.country_code]
      : [];
  if (countryCodes.length) refinementList[ALGOLIA_ATTRS.countryCode] = countryCodes;

  const featuresList = Array.isArray(route.features) ? route.features : route.features ? [route.features] : [];
  if (featuresList.includes("instant")) refinementList.instant_book = ["true"];
  if (featuresList.includes("convertible")) refinementList.convertible = ["true"];
  if (featuresList.includes("automatic")) refinementList.automatic = ["true"];
  // Task #1867 (S2): map the legacy `air_con` route key to the new
  // `has_aircon` refinement attribute (matches Algolia field + DB
  // column). Accept the new key here too so internal callers can use
  // either form.
  if (featuresList.includes("air_con") || featuresList.includes("has_aircon")) {
    refinementList.has_aircon = ["true"];
  }

  const eras = Array.isArray(route.era) ? route.era : route.era ? [route.era] : [];
  if (eras.length) refinementList.eras = eras as string[];

  // `colours` is a comma-joined list of colour ids (task #1835). Accept
  // both shapes (string or array) for forward compatibility.
  const coloursRaw = Array.isArray(route.colours)
    ? route.colours.join(",")
    : (route.colours as string | undefined);
  if (coloursRaw) {
    const ids = coloursRaw.split(",").map(s => s.trim()).filter(Boolean);
    if (ids.length) refinementList.colour_id = ids;
  }

  // Price slider attribute follows rental_type. With no rental type picked we
  // use the rental-type-agnostic waterfall column `filter_price_per_day` so
  // chauffeur-only listings remain reachable. See task #1537.
  const range: Record<string, string> = {};
  if (route.price_min || route.price_max) {
    let priceAttr: string;
    if (route.rental_type === "driving") priceAttr = "price_driving";
    else if (route.rental_type === "event") priceAttr = "price_event";
    else if (route.rental_type === "chauffeured") priceAttr = "chauffeured_min_price_hourly_rate";
    else priceAttr = "filter_price_per_day";
    range[priceAttr] = `${(route.price_min as string) || ""}:${(route.price_max as string) || ""}`;
  }

  let sortBy: string | undefined;
  if (route.sort) {
    const sortMap: Record<string, string> = {
      price_asc: sortIndexName(ALGOLIA_INDEX_NAME, "price_asc"),
      price_desc: sortIndexName(ALGOLIA_INDEX_NAME, "price_desc"),
      newest: sortIndexName(ALGOLIA_INDEX_NAME, "newest"),
      top_rated: sortIndexName(ALGOLIA_INDEX_NAME, "top_rated"),
    };
    sortBy = sortMap[route.sort as string];
  }

  return {
    [ALGOLIA_INDEX_NAME]: {
      query: (route.q as string) || "",
      refinementList,
      range,
      page: route.page ? parseInt(route.page as string) : undefined,
      sortBy,
    },
  };
}

/**
 * Mounts virtual useRefinementList widgets for URL-restorable facets so
 * the Algolia helper has them declared BEFORE the InstantSearch router applies
 * any URL-restored refinements. Without this, loading /search with a
 * rental_type param (e.g. a chauffeured deep link) throws:
 *   "Cannot refine the undeclared facet rental_chauffeured_active"
 * because the router's initial setUiState runs before child components that
 * call useAlgoliaRentalType() have mounted.
 *
 * The same race applies to the colour filter (task #1835): a deep link with
 * `?colours=...` restores a `colour_id` refinement before ColourFilter's
 * useAlgoliaColours() widget mounts (it lives inside a lazily-opened
 * popover), throwing "Cannot refine the undeclared facet colour_id"
 * (Sentry DRIVESHARE_V2-E7). Declare it here with the same OR semantics
 * as useAlgoliaColours so the pre-mount refinement is legal.
 */
function DeclareRentalTypeFacets() {
  useRefinementList({ attribute: ALGOLIA_ATTRS.rentalDriving });
  useRefinementList({ attribute: ALGOLIA_ATTRS.rentalChauffeured });
  useRefinementList({ attribute: ALGOLIA_ATTRS.rentalEvent });
  useRefinementList({ attribute: ALGOLIA_ATTRS.countryCode });
  useRefinementList({ attribute: ALGOLIA_ATTRS.colourId, operator: "or" });
  useRefinementList({ attribute: ALGOLIA_ATTRS.carTypes, operator: "or", limit: 50 });
  // URL routing restores these before the lazily-rendered Make/Era filters
  // mount. Pre-declare them here so InstantSearch never tries to refine an
  // undeclared facet during initial navigation (Sentry V2-HT / V2-HS).
  useRefinementList({ attribute: ALGOLIA_ATTRS.make, limit: 200 });
  useRefinementList({ attribute: ALGOLIA_ATTRS.eras, operator: "or", limit: 20 });
  return null;
}

interface AlgoliaSearchProviderProps {
  children: ReactNode;
}

function SearchDisposalGuard() {
  const instance = useInstantSearchContext();
  useEffect(() => {
    guardSearchDisposal(instance);
    // Disposal is deferred until after React unmounts this child. The guard
    // must stay attached to this instance; it never handles live-query errors.
  }, [instance]);
  return null;
}

export default function AlgoliaSearchProvider({ children }: AlgoliaSearchProviderProps) {
  const searchClient = useMemo(() => {
    if (!ALGOLIA_APP_ID || !ALGOLIA_SEARCH_KEY) return null;
    const client = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY);
    return {
      ...client,
      search: async (...args: Parameters<typeof client.search>) => {
        // InstantSearch versions call the client with either the legacy
        // `search(requests[])` API or Algolia v5's `{ requests }` argument.
        // Capture immutable request state synchronously before awaiting: the
        // helper can mutate SearchResults._state optimistically in between.
        const rawRequests = extractAlgoliaRequests(args);
        const requestStamps = captureAlgoliaRequestStamps(rawRequests);
        // The fallback translator expects the legacy nested params shape,
        // even when the incoming v5 request carries flattened parameters.
        const dates = new URLSearchParams(window.location.search);
        const requests = requestStamps.map(({ indexName, params }) => {
          const dateFrom = dates.get("date_from");
          const dateTo = dates.get("date_to");
          if (!dateFrom && !dateTo) return { indexName, params };
          if (typeof params === "string") {
            const serialized = new URLSearchParams(params);
            if (dateFrom) serialized.set("date_from", dateFrom);
            if (dateTo) serialized.set("date_to", dateTo);
            return { indexName, params: serialized.toString() };
          }
          return { indexName, params: {
            ...(params && typeof params === "object" ? params : {}),
            ...(dateFrom ? { date_from: dateFrom } : {}),
            ...(dateTo ? { date_to: dateTo } : {}),
          } };
        }) as Parameters<typeof runSupabaseFallback>[0];
          // The shared live index cannot facet legacy Morris/Austin Moke
          // models. Use the canonical API for the complete result set, before
          // pagination, instead of merging or injecting individual listings.
          if (requestStamps.some(({ params }) => requiresCanonicalMakeSearch(params))) {
            const fallback = await runSupabaseFallback(requests);
            return stampAlgoliaSearchResponse(fallback, requestStamps) as unknown as Awaited<ReturnType<typeof client.search>>;
          }
        try {
          const routedRequests = rawRequests.map(request => {
            const indexName = bacActivityIndexFor(request, ALGOLIA_INDEX_NAME, import.meta.env.VITE_BAC_HOST_ACTIVITY_INDEX_NAME);
            if (!indexName) return request;
            const nested = request.params && typeof request.params === "object";
            const params = bacLocalityParams(nested ? request.params as Record<string, unknown> : request);
            // No precise user-selected locality (or unsupported geometry):
            // keep the canonical order rather than promote distant hosts.
            return params ? nested ? { ...request, indexName, params } : { ...params, indexName } : request;
          });
          const first = args[0];
          const routedArgs = [
            Array.isArray(first) ? routedRequests : { ...first, requests: routedRequests },
            ...args.slice(1),
          ] as Parameters<typeof client.search>;
          const aliasArgs = expandAlgoliaMakeSearchArgs(routedArgs) as Parameters<typeof client.search>;
          const res = await withTimeout(client.search(...aliasArgs));
          await withTimeout(enrichCanonicalSearchResponse(res));
          clearFallback();
          return stampAlgoliaSearchResponse(res, requestStamps) as typeof res;
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : String(err);
          if (msg.includes("does not exist") || msg.includes("IndexNotFoundException")) {
            console.warn("[Algolia] Index not found, returning empty results:", msg);
            const emptyResponse = {
              results: requestStamps.map(() => ({
                hits: [],
                nbHits: 0,
                page: 0,
                nbPages: 0,
                hitsPerPage: 20,
                processingTimeMS: 0,
                query: "",
                params: "",
              })),
            };
            return stampAlgoliaSearchResponse(emptyResponse, requestStamps) as Awaited<ReturnType<typeof client.search>>;
          }
          if (shouldFallback(err)) {
            reportFallbackTrigger(err);
            const fallback = await runSupabaseFallback(requests);
            return stampAlgoliaSearchResponse(fallback, requestStamps) as unknown as Awaited<ReturnType<typeof client.search>>;
          }
          throw err;
        }
      },
    };
  }, []);

  if (!searchClient) return <>{children}</>;

  const routing = {
    router: history<RouteState>({
      cleanUrlOnDispose: false,
      // Coalesce rapid refinement/keystroke writes into a single history
      // mutation. Firefox throttles combined push/replaceState to 100 per
      // 10s; a generous write delay keeps InstantSearch well clear of it.
      writeDelay: 700,
      getLocation: () => window.location,
      createURL({ qsModule, routeState, location }) {
        const qs = qsModule.stringify(routeState, { arrayFormat: "repeat", encode: false });
        const pathname = location.pathname.startsWith("/") ? location.pathname : "/";
        // InstantSearch only owns the refinement params. The map viewport
        // (`bounds_*`) and the browse country anchor (`country`) are written
        // by other systems on this page. Preserve them here so a refinement
        // write doesn't strip them from the URL — otherwise `setMapBounds`
        // would re-add the bounds on the next render, producing a tit-for-tat
        // history-write fight that helps blow Firefox's throttle.
        const current = new URLSearchParams(location.search);
        const preserved = new URLSearchParams();
        for (const k of ["bounds_north", "bounds_south", "bounds_east", "bounds_west", "country"]) {
          const v = current.get(k);
          if (v) preserved.set(k, v);
        }
        const preservedQs = preserved.toString();
        const combined = [qs, preservedQs].filter(Boolean).join("&");
        return `${pathname}${combined ? `?${combined}` : ""}`;
      },
      // Transient filter churn shouldn't push a new history entry per
      // keystroke/refinement, and the router's own `shouldWrite` no-op guard
      // is defeated by our relative `createURL` (it compares a relative URL
      // against the absolute `location.href`, so it never matches). Override
      // `push` to (a) skip genuine no-ops and (b) use `replaceState` so the
      // back button steps through real navigations, not per-keystroke churn.
      push(url: string) {
        replaceStateIfChanged(url);
      },
      parseURL({ qsModule, location }) {
        return qsModule.parse(location.search.slice(1), { arrayLimit: 50 }) as RouteState;
      },
    }),
    stateMapping: {
      stateToRoute,
      routeToState,
    },
  };

  return (
    <InstantSearch
      searchClient={searchClient as never}
      indexName={ALGOLIA_INDEX_NAME}
      routing={routing}
      future={{ preserveSharedStateOnUnmount: true }}
    >
      <SearchDisposalGuard />
      <DeclareRentalTypeFacets />
      {children}
    </InstantSearch>
  );
}
